import { useCallback, useEffect, useState } from "react";
import { LoginScreen } from "./components/shell/LoginScreen";
import { AppShell, type ShellTab } from "./components/shell/AppShell";
import { TrainingTab, type NextExecutionInfo } from "./components/TrainingTab";
import { CheckinsTab } from "./components/CheckinsTab";
import { StudentsTab } from "./components/StudentsTab";
import { StudentProfilePage } from "./components/StudentProfilePage";
import { AutomationTab } from "./components/AutomationTab";
import { AdminTab } from "./components/AdminUsersPage";
import { NotFoundPage } from "./components/NotFoundPage";
import "./index.css";

type AccountStatus = "pending" | "approved" | "suspended";

interface StatusData {
  credential: { email: string; tokenExp: number | null; hasToken: boolean } | null;
  accountStatus: AccountStatus;
  isAdmin: boolean;
  canUseApp: boolean;
  targets: number;
  hasTarget: boolean;
  nextExecution: (NextExecutionInfo & { targetId: number }) | null;
  recentAttempts: {
    id: number;
    target_date: string;
    activity_name: string;
    started_at: string;
    status: string;
    duration_ms: number | null;
    response_summary: string | null;
    error: string | null;
    retry_count: number;
    run_count: number;
  }[];
}

function isKnownRoute(path: string): boolean {
  const clean = path.replace(/\/+$/, "") || "/";
  return clean === "/" || clean === "/admin";
}

function tabFromHash(): ShellTab {
  const hash = window.location.hash.replace(/^#/, "").split("?")[0] ?? "";
  if (hash === "aulas" || hash === "alunos" || hash === "automacao" || hash === "admin") return hash;
  return "treino";
}

/** `#aluno/<id>` opens the student profile; `/anon` marks a roster-anonymised person. */
function studentFromHash(): { id: number; anon: boolean } | null {
  const hash = window.location.hash.replace(/^#/, "");
  const match = /^aluno\/(\d+)(\/anon)?$/.exec(hash);
  if (!match || !match[1]) return null;
  const id = Number.parseInt(match[1], 10);
  return Number.isFinite(id) ? { id, anon: Boolean(match[2]) } : null;
}

function LoadingScreen() {
  return (
    <div style={{ minHeight: "100svh", display: "grid", placeItems: "center" }}>
      <div className="cb-row" style={{ gap: "0.6rem" }}>
        <span
          style={{
            width: 22,
            height: 22,
            border: "2px solid var(--cb-border2)",
            borderTopColor: "var(--cb-accent)",
            borderRadius: "50%",
            animation: "cb-spin 0.8s linear infinite",
          }}
        />
        <span className="cb-label">verificando sessão</span>
      </div>
    </div>
  );
}

function PendingScreen({ status, onLogout }: { status: StatusData; onLogout: () => void }) {
  const suspended = status.accountStatus === "suspended";
  return (
    <div style={{ minHeight: "100svh", display: "grid", placeItems: "center", padding: "2rem 1.25rem" }}>
      <div className="cb-panel cb-rise" style={{ width: "100%", maxWidth: "26rem" }}>
        <div className="cb-panel__body" style={{ display: "grid", gap: "1.1rem" }}>
          <div>
            <span className="cb-brand">
              CHECK<span style={{ color: "var(--cb-accent)" }}>BUN</span>
            </span>
            <p className="cb-display cb-hero-title" style={{ margin: "1.1rem 0 0.4rem", fontSize: "2.2rem" }}>
              {suspended ? "Conta suspensa" : "Aguardando aprovação"}
            </p>
            <p style={{ color: "var(--cb-muted)", margin: 0, fontSize: "0.9rem" }}>
              {suspended
                ? "O acesso foi suspenso. Fale com um administrador do checkbun."
                : "Sua conta ainda não foi liberada. Um administrador precisa aprovar antes do primeiro check-in."}
            </p>
          </div>
          <div className="cb-kv" style={{ borderStyle: "solid" }}>
            <span className="cb-kv__k">email</span>
            <span className="cb-kv__v">{status.credential?.email ?? "—"}</span>
          </div>
          <button type="button" className="cb-btn cb-btn--ghost" onClick={onLogout}>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}

export function App() {
  const [routePath] = useState(() => window.location.pathname.replace(/\/+$/, "") || "/");
  const [tab, setTab] = useState<ShellTab>(() => tabFromHash());
  const [student, setStudent] = useState<{ id: number; anon: boolean } | null>(() => studentFromHash());
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [status, setStatus] = useState<StatusData | null>(null);

  useEffect(() => {
    const onHash = () => {
      const student = studentFromHash();
      setStudent(student);
      if (!student) setTab(tabFromHash());
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/status");
      if (!res.ok) {
        setAuthed(false);
        setStatus(null);
        return;
      }
      setStatus((await res.json()) as StatusData);
      setAuthed(true);
    } catch {
      setAuthed(false);
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, [authed, refresh]);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setAuthed(false);
    setStatus(null);
  };

  const selectTab = (next: ShellTab) => {
    window.location.hash = next === "treino" ? "" : next;
    setTab(next);
  };

  if (!isKnownRoute(routePath)) {
    return <NotFoundPage onGoHome={() => window.history.pushState(null, "", "/")} />;
  }

  if (authed === null) return <LoadingScreen />;
  if (!authed) return <LoginScreen onSuccess={refresh} />;
  if (!status?.canUseApp) return <PendingScreen status={status!} onLogout={handleLogout} />;

  const visibleTab: ShellTab = !status.isAdmin && (tab === "admin" || tab === "alunos") ? "treino" : tab;

  return (
    <AppShell
      status={status}
      tab={visibleTab}
      onTab={selectTab}
      onLogout={handleLogout}
      automationOn={Boolean(status.hasTarget && status.nextExecution)}
    >
      {student ? (
        <StudentProfilePage
          id={student.id}
          note={student.anon ? "anônimo na turma (identificado pela API)" : undefined}
          onBack={() => window.history.back()}
        />
      ) : (
        <>
          {visibleTab === "treino" && (
            <TrainingTab
              nextExecution={status.nextExecution}
              onOpenAutomation={() => selectTab("automacao")}
              onChanged={refresh}
            />
          )}
          {visibleTab === "aulas" && <CheckinsTab />}
          {visibleTab === "alunos" && <StudentsTab />}
          {visibleTab === "automacao" && <AutomationTab status={status} onChanged={refresh} />}
          {visibleTab === "admin" && <AdminTab onChanged={refresh} />}
        </>
      )}
    </AppShell>
  );
}

export default App;
